import { Drawer, notification, Modal } from 'antd';
import { useState, useEffect, useRef, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from 'antd';
import { ReactComponent as BellIcon } from '../assets/images/icons/bell.svg';
import { ReactComponent as CartIcon } from '../assets/images/icons/cart.svg';
import { ReactComponent as MobileCartIcon } from '../assets/images/icons/mobilecart.svg';
import { ReactComponent as MobileMenuIcon } from '../assets/images/icons/mobilemenu.svg';
import { ReactComponent as MobileNotificationIcon } from '../assets/images/icons/mobilenotification.svg';
import { ReactComponent as Cancel } from '../assets/images/icons/x.svg';
import Logo from '../assets/images/logo.png';
import { addToCart } from '../slices/cartSlice';
import { _get_user_notification } from '../common/axios_services';
import { fetchUserProfile } from '../slices/profileSlice';
import { default as AppRoutes } from "./routes";
import { logoutUser } from '../slices/authSlice';

const Navbar = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector((state) => state.userCart.cart);
  const { dataFetched: userData, loading: profileLoading, fetch: fetchDataStatus } = useSelector((state) => state.userProfile);
  const { isAuthenticated, loading: authLoading } = useSelector((state) => state.auth);
  const isLoggingOut = useSelector(state => state.auth.isLoggingOut);

  const [open, setOpen] = useState(false);
  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const [notificationData, setNotificationData] = useState([]);
  const [fixedNav] = useState(false);
  const [showCashbackModal, setShowCashbackModal] = useState(false);
  const sliderRef = useRef(null);

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = JSON.parse(localStorage.getItem('konnect_cart'));
    if (savedCart) {
      dispatch(addToCart(savedCart));
    }
  }, [dispatch]);

  const handleLogout = async () => {
    setOpen(false);
    setOpenNotificationModal(false);

    // Prevent multiple logout calls
    if (isLoggingOut) {
      console.log('Logout already in progress');
      return;
    }

    try {
      const result = await dispatch(logoutUser()).unwrap();
      console.log('Logout completed:', result.message);
      openNotificationWithIcon('success', 'Logout Successful', 'You have been logged out successfully');
      // Navigation will happen through useEffect when isAuthenticated changes
    } catch (error) {
      console.log('Logout error 1 :', error);
      // openNotificationWithIcon('error', 'Logout Error', 'There was a problem logging out');

      // Even if there's an error, the state should be cleaned up
      // Navigation should still happen since isAuthenticated will be false
    }
  };

  const openNotificationWithIcon = (type, title, message) => {
    notification[type]({
      message: title || '',
      placement: "bottomRight",
      description: message || "",
    });
  };

  // Fetch user profile if authenticated and not already loaded
  useEffect(() => {
    if (isAuthenticated && !userData && !profileLoading) {
      dispatch(fetchUserProfile());
    }
  }, [isAuthenticated, userData, profileLoading, dispatch]);

  const getUserNotifications = async () => {
    if (isAuthenticated && userData) {
      try {
        let userNotifs = await _get_user_notification({ page: 1, per_page: 10 });
        setNotificationData(userNotifs.data.data);
      } catch (err) {
        if (!err.response) {
          openNotificationWithIcon('error', 'Network Error', err?.message);
        }
      }
    }
  };

  // Get user notifications when user data changes
  useEffect(() => {
    if (isAuthenticated && userData) {
      getUserNotifications();
    } else {
      // Reset notification data when user is not authenticated or userData is null
      setNotificationData([]);
    }
  }, [isAuthenticated, userData]);

  // Reset state when auth state changes
  useEffect(() => {
    if (!isAuthenticated) {
      setNotificationData([]);
      // Close drawers to prevent rendering with null userData
      setOpen(false);
      setOpenNotificationModal(false);
    }
  }, [isAuthenticated]);

  const sliderContent = useMemo(() =>
    [...Array(4)].map((_, i) => (
      <h4
        key={i}
        className="text-white text-[14px] cursor-pointer inline-block mx-4"
        onClick={() => setShowCashbackModal(true)}
      >
        Eat & Get ₦15,000 Cash Every Sunday {" "}
        <span style={{ textDecoration: 'underline' }}>how it works</span>
      </h4>
    )), []
  );

  // Loading state
  const isLoading = authLoading || profileLoading;

  // Safe render check for userData
  const userFirstName = userData?.first_name?.trim() || '';
  const isKycVerified = userData?.kyc_status === "verified";
  const isRestaurant = userData?.is_restaurant === true;

  return (
    <div className="nav-display top-0 z-[100] sticky">
      <div ref={sliderRef} className="slider-container bg-[#063F29] overflow-hidden whitespace-nowrap py-2">
        <div className="slider-track inline-block whitespace-nowrap">
          {sliderContent}
        </div>
      </div>

      {/* Cashback Modal */}
      <Modal
        title="How It Works"
        open={showCashbackModal}
        onCancel={() => setShowCashbackModal(false)}
        footer={[
          <button
            key="okay"
            className="bg-[#063F29] text-white px-6 py-2 rounded-lg"
            onClick={() => setShowCashbackModal(false)}
          >
            Okay
          </button>
        ]}
        centered
        className="how-it-works-modal"
      >
        <div className="space-y-4">
          <ul className="list-disc pl-5 space-y-3">
            <li>
              We will credit you <span className="font-semibold">₦15,000 </span> every <span className="font-semibold"> SUNDAY</span>
            </li>
            <li>
              If you are a top spender & spends <span className="font-semibold">₦25,000+ weekly</span>
            </li>
            <li>
              Visit your referral page to check if you qualify to earn.
            </li>
            <li>
              Terms & conditions apply.
            </li>
            <li>
              Eat more & Earn more every Sunday.
            </li>
          </ul>
        </div>
      </Modal>
      <div
        className={`navigation  ${props.noShadow ? 'no-shadow' : ''} ${props.adjustNavSize ? 'adjustPadding' : ''
          } ${fixedNav ? 'fixed' : ''}`}
      >
        <div className="logo_bar">
          <Link to={`/`}>
            <img src={Logo} className="logo" alt="konnect logo" />
          </Link>
          <ul className="desktop">
            <li>
              <Link to="/restaurant">
                <p>Restaurants</p>
              </Link>
            </li>
            <li>
              <Link to={AppRoutes.contact}>
                <p>Contact us</p>
              </Link>
            </li>
            <li>
              <Link to={AppRoutes.terms}>
                <p>Terms</p>
              </Link>
            </li>
            <li>
              <Link to={AppRoutes.policies}>
                <p>Policy</p>
              </Link>
            </li>
          </ul>
        </div>
        <div className="desktop">
          <ul>
            <li className="navi">
              {isAuthenticated ? (
                <div onClick={() => setOpenNotificationModal(true)}>
                  <Badge count={notificationData.length}>
                    <BellIcon style={{ height: '2rem' }} />
                  </Badge>
                </div>
              ) : (
                <Link to={`${AppRoutes.signin}`}>
                  <BellIcon style={{ height: '2rem' }} />
                </Link>
              )}
            </li>
            {
              props.showCart && (
                <li className="navi cart-absolute">
                  <Link className="cartIcon" to={props.restaurantCart ? `/restaurant/1` : `/cart`}>
                    {cart ? (
                      <Badge count={cart.data?.length || 0}>
                        <CartIcon style={{ height: '2rem' }} />
                      </Badge>
                    ) : (
                      <Badge count={0}>
                        <CartIcon style={{ height: '2rem' }} />
                      </Badge>
                    )}
                  </Link>
                </li>
              )
            }

            {isAuthenticated && userData ? (
              <li style={{ paddingRight: 0 }} className="navi cart-absolute">
                <Link to={AppRoutes.profile_loyalty} className="button_like">
                  <span>
                    Hi, {userFirstName}
                  </span>
                </Link>
              </li>
            ) : (
              <span>
                <li>
                  <Link className="auth-na" to={`${AppRoutes.signin}`}>
                    Sign In
                  </Link>
                </li>
                <li>
                  <Link className="auth-na button_like" to={`${AppRoutes.signup}`}>
                    Sign Up
                  </Link>
                </li>
              </span>
            )}
          </ul>
        </div>
        <div className="mobile show-all">
          <div className="other-nav">
            {
              props.showCart && (
                <div>
                  <span className="navi cart-absolute">
                    <Link className="cartIcon" to={`${AppRoutes.cart}`}>
                      {cart ? (
                        <Badge count={cart.data?.length || 0}>
                          <MobileCartIcon style={{ height: '2.3rem' }} />
                        </Badge>
                      ) : (
                        <Badge count={0}>
                          <MobileCartIcon style={{ height: '2.3rem' }} />
                        </Badge>
                      )}
                    </Link>
                  </span>
                </div>
              )
            }
            <div>
              {isAuthenticated ? (
                <div onClick={() => setOpenNotificationModal(true)}>
                  <Badge count={notificationData.length}>
                    <MobileNotificationIcon style={{ height: '2rem' }} />
                  </Badge>
                </div>
              ) : (
                <Link to={`${AppRoutes.signin}`}>
                  <MobileNotificationIcon className="auth-na" style={{ height: '2.5rem' }} />
                </Link>
              )}
            </div>
            <div className="lastNav" onClick={() => setOpen(true)}>
              <MobileMenuIcon style={{ height: '2.5rem' }} />
            </div>
          </div>
        </div>
      </div>
            
      {/* Only render drawers if they're open to prevent errors when userData is null */}
      {open && (
        <Drawer
          title={
            <div className="mobile-grid">
              <div>
                <Link to="/">
                  <img src={Logo} style={{ maxWidth: "135px" }} alt="logo" className="logo" />
                </Link>
              </div>
              <Cancel onClick={() => onClose()} className="cancel" />
            </div>
          }
          placement={'left'}
          closable={false}
          onClose={onClose}
          open={open}
          key={'right'}
        >
          <div className="mobile-bottom-nav">
            <ul>
              {!isAuthenticated && (
                <li onClick={() => { onClose(); }}>
                  <Link to={AppRoutes.restaurant} >
                    <p>Restaurants</p>
                  </Link>
                </li>
              )}
              {isAuthenticated && userData ? (
                <>
                  <li>
                    <div>
                      <h2>Hi, {userFirstName}</h2>
                    </div>
                  </li>
                  {isRestaurant && (
                    <li onClick={() => { onClose(); }}>
                      <Link to="/restaurant/dashboard">
                        <p>My Restaurant</p>
                      </Link>
                    </li>
                  )}
                  <li onClick={() => { onClose(); }}>
                    <Link to={AppRoutes.restaurant} >
                      <p>Restaurants</p>
                    </Link>
                  </li>
                  {isKycVerified && (
                    <li onClick={() => { onClose(); }}>
                      <Link to="/profile/loyalty">
                        <p style={{ fontSize: "14px" }}>Wallet</p>
                      </Link>
                    </li>
                  )}
                  {isKycVerified && (
                    <li onClick={() => { onClose(); }}>
                      <Link to={AppRoutes.profile_referral}>
                        <p>Referral </p>
                      </Link>
                    </li>
                  )}
                  <li onClick={() => { onClose(); }}>
                    <Link to={AppRoutes.profile_orders}>
                      <p>Order</p>
                    </Link>
                  </li>
                  <li onClick={() => { onClose(); }}>
                    <Link to="/profile/overview">
                      <p>Profile</p>
                    </Link>
                  </li>
                </>
              ) : (
                <li onClick={() => { onClose(); }}>
                  <Link to={`${AppRoutes.signin}`}>
                    <p>Sign in</p>
                  </Link>
                </li>
              )}
              <li onClick={() => { onClose(); }}>
                <Link to={AppRoutes.contact}>
                  <p>Contact us</p>
                </Link>
              </li>
              <li onClick={() => { onClose(); }}>
                <Link to={AppRoutes.terms}>
                  <p>Terms</p>
                </Link>
              </li>
              <li onClick={() => { onClose(); }}>
                <Link to={AppRoutes.policies}>
                  <p>Policy</p>
                </Link>
              </li>
              {isAuthenticated && userData && (
                <li>
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogout();
                    }}
                  >
                    <p style={{ fontWeight: "600" }}>Sign Out</p>
                  </Link>
                </li>
              )}
            </ul>
            {!isAuthenticated && !isLoading ? (
              <div className="bottom-link">
                <Link to={AppRoutes.signup}>Create a free account</Link>
              </div>
            ) : null}
          </div>
        </Drawer>
      )}
      {openNotificationModal && (
        <Drawer
          title={
            <div className="mobile-grid nav-notif-grid">
              <Link to="/">
                <img src={Logo} style={{ maxWidth: "135px" }} alt="logo" className="logo" />
              </Link>
              <div style={{ cursor: 'pointer' }}>
                <Cancel onClick={() => setOpenNotificationModal(false)} className="cancel" />
              </div>
            </div>
          }
          placement={'right'}
          closable={false}
          onClose={() => setOpenNotificationModal(false)}
          open={openNotificationModal}
          key={'notifDrawer'}
        >
          <div className="notifData">
            {notificationData.length ? (
              <div></div>
            ) : (
              <div className="emptyData">
                <p>You have no new notifications</p>
              </div>
            )}
          </div>
        </Drawer>
      )}
    </div>
  );
};

export default Navbar;